import { Fragment } from 'react';
import {
	PagedDialog,
	H3,
	H4,
	Spacer,
	Text,
	Label,
	TempChip,
	InfoChip,
	CargoBreakdownList,
	SeparatedList
} from '@components';
import {
	TShipmentOrder,
	CHIP_SIZE,
	CARGO_TYPE,
	FREIGHT_MODE,
	LTL_FAMILY_CARGO_TYPES
} from '@types';
import { EditShipment } from '@state';
import { useRecoilValue } from 'recoil';
import {
	formatDate,
	getBookedShipmentCargoTypeLabel,
	getCargoQuantitySegments,
	getCargoRows,
	getCargoTypeFromCargoTypeId,
	getCargoTypeListLabel,
	getFreightModeLabel,
	getCargoBreakdownListItems,
	groupCargosByStop,
} from '@utils';
import ArrowUpIcon from '@mui/icons-material/NorthEast';
import ArrowDownIcon from '@mui/icons-material/SouthEast';
import styles from './EditShipment.module.scss';

export const Preview = () => {
	const shipment = useRecoilValue<TShipmentOrder | null>(EditShipment);
	const getRefs = () => {
		const orderNumbers: string[] = [];
		if (shipment) {
			if (shipment.shipper_ref1 && shipment.shipper_ref1 !== '') orderNumbers.push(shipment.shipper_ref1);
			if (shipment.shipper_ref2 && shipment.shipper_ref2 !== '') orderNumbers.push(shipment.shipper_ref2);
			if (shipment.shipper_ref3 && shipment.shipper_ref3 !== '') orderNumbers.push(shipment.shipper_ref3);
		}
		return orderNumbers.join(', ');
	}
	const shipmentCargoType = shipment ? getCargoTypeFromCargoTypeId(shipment.cargo_type_id) as CARGO_TYPE : undefined;
	const isLTLFamilyCargo = !!shipmentCargoType && LTL_FAMILY_CARGO_TYPES.includes(shipmentCargoType);
	const cargoRows = getCargoRows(shipment?.cargos ?? []);

	/* "LTL | 12T | Rigid | Road | Bundle/Pallet" - capacity, truck size, truck type, freight mode, then the row
	   types present (joined when mixed). Mirrors the Reorder Shipment side panel. */
	const getLoadTruckDetails = () => {
		if (!shipment) return [];
		return [
			getBookedShipmentCargoTypeLabel(shipmentCargoType),
			shipment.req_truck_size?.name,
			shipment.req_truck_type?.name,
			getFreightModeLabel(shipment.freight_mode as FREIGHT_MODE),
			isLTLFamilyCargo ? getCargoTypeListLabel(cargoRows) : ''
		];
	}

	// "4 Qty | 4 spaces | 3 m³ | 200kg" - the shipment's totals regardless of capacity type.
	const getCargoQuantityDetails = () => getCargoQuantitySegments(cargoRows);

	// Each cargo gets its own line once there are 2+, as their dimensions will differ.
	const showCargoBreakdown = (shipment?.cargos?.length ?? 0) >= 2;
	const getDateTimes = (dest: 'pickup' | 'dropoff', index: number) => {
		const theDate = formatDate(new Date(shipment?.cargos[index][dest === 'pickup' ? 'pickup_at' : 'drop_at'] ?? new Date()), 'DATE_WITH_SHORT_MONTH_NAME');
		const timeFrom = dest === 'pickup' ? shipment?.cargos[index].pick_from : shipment?.cargos[0].drop_from;
		const timeTo = dest === 'pickup' ? shipment?.cargos[index].pick_to : shipment?.cargos[0].drop_to;
		return `${theDate} | ${timeFrom} - ${timeTo}`;
	}
	const getDestDetails = (dest: 'pickup' | 'dropoff', index: number) => {
		return (
			<>
				<div className={`${styles.row} ${styles.spaceBetween}`}>
					<Label>{shipment?.cargos[index][dest].business_name}</Label>
					<InfoChip text={dest === 'pickup' ? 'Pick-up' : 'Drop-off'} size={CHIP_SIZE.SMALL} CustomIcon={dest === 'pickup'? ArrowUpIcon : ArrowDownIcon} />
				</div>
				<Text>{shipment?.cargos[index][dest].address}</Text>
				<Text>{getDateTimes(dest, index)}</Text>
			</>
		)
	}
	return (
		<PagedDialog.Preview>
			<H3>Booking Details</H3>
			<Spacer size={20} divider />
			<H4>{shipment?.reference}</H4>
			<Spacer size={5} />
			<Text>{getRefs()}</Text>
			<Spacer size={5} />
			<SeparatedList items={[<TempChip key={'temperature'} name={shipment?.temperature_id ?? 1} size={CHIP_SIZE.SMALL} />, ...getLoadTruckDetails()]} />
			<Spacer size={8} />
			<SeparatedList items={getCargoQuantityDetails()} />
			{showCargoBreakdown && <>
				<Spacer size={8} />
				<CargoBreakdownList items={getCargoBreakdownListItems(cargoRows)} />
			</>}
			<Spacer size={20} divider />
			{/* Cargos collected from the same place are one stop, and there is a single drop-off
				however many cargos the shipment carries. */}
			{groupCargosByStop(shipment?.cargos ?? []).pickups.map((group, i) => {
				return (
					<Fragment key={i}>
						{getDestDetails('pickup', group[0])}
						<Spacer size={20} />
					</Fragment>
				)
			})}
			{!!shipment?.cargos.length && getDestDetails('dropoff', shipment.cargos.length - 1)}
		</PagedDialog.Preview>
	);
}
Preview.displayName = 'Preview';
